import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Collapse, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import SimpleDropdown from '~/modules/common/components/SimpleDropdown';
import { ResourceAllocationsToReschedule } from '~/types';
import { getTextInBold } from '~/modules/common/util';

const labelId = 'reschedule-allocated-time-by-label';

const inputLabelProps = {
  id: labelId
};

const useCollapseStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(0.5, 0, 0, 3.5),
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0
    }
  }
}));

const useTaskWarningStyles = makeStyles(theme => ({
  root: {
    ...theme.typography.caption,
    padding: theme.spacing(0, 2, 0, 1),
    margin: theme.spacing(0.5, 0, 0, 3),
    maxWidth: 'fit-content'
  }
}));

const secondaryTypographyProps = {
  variant: 'caption'
};

const getOptions = formatMessage => [
  {
    id: ResourceAllocationsToReschedule.RemoveAllocatedTime,
    displayText: formatMessage({
      id:
        'rescheduleProject.rescheduleAllocatedTimeBy.removeAllocatedTime.title'
    }),
    component: (
      <ListItemText
        primary={formatMessage({
          id:
            'rescheduleProject.rescheduleAllocatedTimeBy.removeAllocatedTime.title'
        })}
        secondary={formatMessage({
          id:
            'rescheduleProject.rescheduleAllocatedTimeBy.removeAllocatedTime.description'
        })}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    )
  },
  {
    id: ResourceAllocationsToReschedule.ReleaseAllResources,
    displayText: formatMessage({
      id:
        'rescheduleProject.rescheduleAllocatedTimeBy.releaseAllResources.title'
    }),
    component: (
      <ListItemText
        primary={formatMessage({
          id:
            'rescheduleProject.rescheduleAllocatedTimeBy.releaseAllResources.title'
        })}
        secondary={formatMessage({
          id:
            'rescheduleProject.rescheduleAllocatedTimeBy.releaseAllResources.description'
        })}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    )
  }
];

export const ResourceAllocationsToRescheduleDropDown = ({
  isRescheduleResourceAllocationsChecked,
  onChange,
  value
}) => {
  const { formatMessage } = useIntl();
  const collapseClasses = useCollapseStyles();
  const taskWarningClasses = useTaskWarningStyles();

  const options = getOptions(formatMessage);

  const selectedValue = useMemo(() => options.find(x => x.id === value), [
    options,
    value
  ]);

  const selectProps = useMemo(
    () => ({
      id: 'reschedule-allocated-time-by',
      labelId,
      SelectDisplayProps: {
        role: 'combobox'
      },
      renderValue: valueObj =>
        options.find(({ id }) => id === valueObj).displayText
    }),
    [options]
  );

  return (
    <>
      <Collapse
        in={isRescheduleResourceAllocationsChecked}
        timeout="auto"
        classes={collapseClasses}
        unmountOnExit
      >
        <SimpleDropdown
          label={formatMessage({
            id: `rescheduleProject.rescheduleAllocatedTimeBy.label`
          })}
          variant="outlined"
          margin="dense"
          options={options}
          onChange={onChange}
          value={selectedValue}
          noneOption={false}
          disableSort
          required
          selectProps={selectProps}
          inputLabelProps={inputLabelProps}
        />
      </Collapse>
      {isRescheduleResourceAllocationsChecked && (
        <Alert
          icon={false}
          severity="warning"
          className={taskWarningClasses.root}
          variant="standard"
        >
          <FormattedMessage
            id="rescheduleProject.rescheduleTasksWarning"
            values={getTextInBold()}
          />
        </Alert>
      )}
    </>
  );
};

ResourceAllocationsToRescheduleDropDown.propTypes = {
  isRescheduleResourceAllocationsChecked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOf([
    ResourceAllocationsToReschedule.RemoveAllocatedTime,
    ResourceAllocationsToReschedule.ReleaseAllResources
  ])
};

export default ResourceAllocationsToRescheduleDropDown;
